import { useAppConfig } from "@memorang/ui/contexts/AppConfigContext";
import {
	authHeaders,
	useAuthTokenGetter,
} from "@memorang/ui/contexts/AuthTokenContext";
import { useQuery } from "@tanstack/react-query";
import { Platform } from "react-native";
//@ts-ignore
import packageJson from "../../../package.json";
import type { RecommendedQuestions } from "../../types";
import {
	ENDPOINT_RECOMMENDED_QUERIES,
	RECOMMENDED_QUESTIONS_QUERY_KEY,
} from "../constants";
import type {
	EpubParams,
	ItemCacheIdentity,
	VectorStoreParams,
} from "../types";

type Props = {
	userPrompt: string;
	outputLanguage: string;
	userId?: string;
	vectorStoreParams?: VectorStoreParams;
	epubParams?: EpubParams;
	defaultRecommendedQueries?: string[];
	threadId?: string;
	loading?: boolean;
	appId?: string;
	cacheIdentity?: ItemCacheIdentity | null;
};
const version = packageJson.version;

// Identity contract keys the query on stable item dimensions so streamed
// feedback updating the prompt cannot churn the key into request spam.
// Shared with useSearch, which reads this cache without its own queryFn —
// both sides must derive the key here or they silently drift apart.
export const resolveRecommendedQueriesCacheKey = ({
	userPrompt,
	cacheIdentity,
	examId,
	itemId,
	outputLanguage,
}: {
	userPrompt?: string;
	cacheIdentity?: ItemCacheIdentity | null;
	examId?: string | number;
	itemId?: string;
	outputLanguage: string;
}) => [
	RECOMMENDED_QUESTIONS_QUERY_KEY,
	cacheIdentity === undefined
		? userPrompt
		: (cacheIdentity ?? ["uncached", examId, itemId, outputLanguage]),
];

export const useRecommendedQueries = ({
	userPrompt,
	vectorStoreParams,
	userId,
	epubParams,
	defaultRecommendedQueries,
	threadId,
	loading,
	outputLanguage,
	appId,
	cacheIdentity,
}: Props) => {
	const getAuthToken = useAuthTokenGetter();
	const { ai } = useAppConfig();
	const { data, isLoading, isRefetching } = useQuery<RecommendedQuestions>({
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		staleTime: 5 * 60 * 1000,
		enabled: !loading && Boolean(ai?.aila),
		...(defaultRecommendedQueries
			? {
					initialData: {
						topic: "",
						followUps: defaultRecommendedQueries || [],
					},
				}
			: {}),
		queryKey: resolveRecommendedQueriesCacheKey({
			userPrompt,
			cacheIdentity,
			examId: vectorStoreParams?.examId,
			itemId: vectorStoreParams?.itemId,
			outputLanguage,
		}),
		queryFn: async ({ signal }) => {
			if (!vectorStoreParams) {
				return {
					topic: "",
					followUps: [],
				};
			}
			const usesIdentityContract = cacheIdentity !== undefined;
			const finalCacheKey = usesIdentityContract
				? undefined
				: `${version} : ${JSON.stringify(vectorStoreParams)} : ${userPrompt}`;
			const token = await getAuthToken();
			const result = await fetch(ENDPOINT_RECOMMENDED_QUERIES, {
				signal,
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...authHeaders(token),
				},
				body: JSON.stringify({
					userPrompt,
					...(usesIdentityContract
						? { cacheIdentity }
						: { cacheKey: finalCacheKey }),
					examId: `${vectorStoreParams.examId}`,
					userId,
					platform: Platform.OS,
					pageText: epubParams?.pageText,
					selectedText: epubParams?.selectedText,
					threadId,
					outputLanguage,
					appId,
				}),
			});
			if (!result.ok) {
				throw new Error("Failed to fetch recommended queries");
			}
			const body = await result.json();
			return body;
		},
	});

	return {
		isLoading: isLoading || isRefetching,
		recommendedQueries: data?.followUps.slice(0, 3) ?? [],
		recommendedTitle: data?.topic ?? "",
	};
};
